import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import styles from './ConversationalMode.module.scss'
import ConversationWindow from './ConversationWindow/ConversationWindow.tsx'
import ConversationInput from './ConversationInput/ConversationInput.tsx'
import ChatSearchModal from './ChatSearchModal/ChatSearchModal.tsx'
import ConversationHistorySidebar from './ConversationHistorySidebar/ConversationHistorySidebar.tsx'
import { Message, ConversationSession } from '../../../../utils/conversationStorage.ts'
import { getDesktopSuggestions } from '../dashboardActionsConfig.tsx'
import type { DesktopSuggestion } from '../dashboardActionsConfig.tsx'

interface ConversationalModeProps {
  isActive: boolean
  isMobile?: boolean
  messages: Message[]
  onSendMessage: (message: string, options?: { confirmExecution?: boolean }) => Promise<void>
  onClose: () => void
  isLoading?: boolean
  canSendMessage: boolean
  remainingMessages: number
  hasReachedLimit: boolean
  limit: number
  matchedSuggestion?: DesktopSuggestion | null
  onSuggestionAction?: () => void
  allSessions: ConversationSession[]
  currentSessionId: string | null
  onSelectSession: (sessionId: string) => void
  onCreateNewSession: () => void
  onDeleteSession: (sessionId: string) => void
  userId?: string
  userData?: any
  searchModalRef?: React.MutableRefObject<(() => void) | null>
  onSidebarStateChange?: (isCollapsed: boolean) => void
  missingFields?: string[]
  readyToExecute?: boolean
  hasPendingImage?: boolean
  imageUploadAcknowledged?: boolean
  pendingImagePreview?: string | null
  onImageSelect?: (file: File) => void
  onImageClear?: () => void
  onConfirmExecution?: () => void
}

const ConversationalMode: React.FC<ConversationalModeProps> = ({
  isActive,
  isMobile = false,
  messages,
  onSendMessage,
  onClose,
  isLoading = false,
  canSendMessage,
  remainingMessages,
  hasReachedLimit,
  limit,
  matchedSuggestion,
  onSuggestionAction,
  allSessions,
  currentSessionId,
  onSelectSession,
  onCreateNewSession,
  onDeleteSession,
  userId,
  userData,
  searchModalRef,
  onSidebarStateChange,
  missingFields = [],
  readyToExecute = false,
  hasPendingImage = false,
  imageUploadAcknowledged = false,
  pendingImagePreview = null,
  onImageSelect,
  onImageClear,
  onConfirmExecution
}) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [inputValue, setInputValue] = useState('')
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false)
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false)

  useEffect(() => {
    if (isActive) {
      setIsSidebarCollapsed(false)
      onSidebarStateChange?.(false)
    }
  }, [isActive, onSidebarStateChange])

  const handleToggleSidebar = () => {
    const newState = !isSidebarCollapsed
    setIsSidebarCollapsed(newState)
    onSidebarStateChange?.(newState)
  }

  const allSuggestions = useMemo(() => {
    return getDesktopSuggestions(navigate, userId, userData, dispatch)
  }, [navigate, userId, userData, dispatch])

  const openSearchModal = () => {
    setIsSearchModalOpen(true)
  }

  useEffect(() => {
    if (searchModalRef) {
      searchModalRef.current = openSearchModal
      return () => {
        searchModalRef.current = null
      }
    }
  }, [searchModalRef])

  if (!isActive) {
    return null
  }

  const handleSend = async () => {
    if ((!inputValue.trim() && !hasPendingImage) || isLoading || !canSendMessage) {
      return
    }

    const message = inputValue.trim()
    setInputValue('')
    await onSendMessage(message || 'Imagen cargada')
  }

  const handleConfirmExecution = async () => {
    if (isLoading) return
    setInputValue('')
    await onConfirmExecution?.()
  }

  const showImageUpload = missingFields.includes('image_uploaded')

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const rootClassName = [
    styles.conversationalMode,
    isMobile ? styles.conversationalModeMobile : styles.conversationalModeDesktop,
    !isMobile && isSidebarCollapsed ? styles.sidebarCollapsed : ''
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={rootClassName} data-desktop={!isMobile || undefined}>
      {!isMobile && (
        <ConversationHistorySidebar
          sessions={allSessions}
          currentSessionId={currentSessionId}
          onSelectSession={onSelectSession}
          onCreateNewSession={onCreateNewSession}
          onDeleteSession={onDeleteSession}
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={handleToggleSidebar}
          userId={userId}
          userData={userData}
          onOpenSearchModal={openSearchModal}
        />
      )}
      <div className={styles.conversationContent}>
        <ConversationWindow
          messages={messages}
          onClose={onClose}
          isLoading={isLoading}
          matchedSuggestion={matchedSuggestion}
          onSuggestionAction={onSuggestionAction}
          allSuggestions={allSuggestions}
          hasReachedLimit={hasReachedLimit}
          remainingMessages={remainingMessages}
          limit={limit}
          userData={userData}
        />
        <div className={styles.floatingInputContainer}>
          <ConversationInput
            value={inputValue}
            onChange={setInputValue}
            onSend={handleSend}
            onKeyPress={handleKeyPress}
            disabled={isLoading || !canSendMessage}
            placeholder={
              canSendMessage
                ? 'Escribí tu mensaje...'
                : 'Has alcanzado el límite de mensajes'
            }
            showImageUpload={showImageUpload}
            hasPendingImage={hasPendingImage}
            imageUploadAcknowledged={imageUploadAcknowledged}
            pendingImagePreview={pendingImagePreview}
            onImageSelect={onImageSelect}
            onImageClear={onImageClear}
            readyToExecute={readyToExecute}
            onConfirmExecution={readyToExecute ? handleConfirmExecution : undefined}
          />
        </div>
      </div>
      <ChatSearchModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
        sessions={allSessions}
        currentSessionId={currentSessionId}
        onSelectSession={onSelectSession}
        onCreateNewSession={onCreateNewSession}
        onDeleteSession={onDeleteSession}
        userId={userId}
        userData={userData}
      />
    </div>
  )
}

export default ConversationalMode
